/**
 * Time primitives for Lunchly.
 *
 * The whole application represents clock times as "minutes from midnight of the
 * shift's own calendar day". A shift that finishes after midnight simply has an
 * end value greater than 1440 (e.g. 22:00-08:00 => start 1320, end 1920).
 * Keeping a single linear number line makes overlap, duration and coverage maths
 * trivial and removes every timezone/DST concern - we never construct a Date for
 * a clock time, only for calendar dates.
 */

export const MINUTES_PER_DAY = 1440;

/** Ordered day keys. Index 0 is always the week-commencing day (Monday). */
export const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export const DAY_LABELS = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};

export const DAY_SHORT_LABELS = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
};

export const WEEKEND_DAYS = new Set(['sat', 'sun']);

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** True when the day key belongs to the weekend profile. */
export function isWeekend(dayKey) {
  return WEEKEND_DAYS.has(dayKey);
}

/**
 * Parse a clock time into minutes from midnight.
 * Accepts "8:30", "08:30", "0830", "8.30", "8:30am", "20:00", "8pm", "24:00".
 * Returns null when the value cannot be understood.
 */
export function parseTimeToMinutes(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.round(value);
  }

  let text = String(value).trim().toLowerCase();
  if (!text) return null;

  // Normalise unicode separators and stray whitespace.
  text = text.replace(/[‒-―−]/g, '-').replace(/\s+/g, '');

  let meridiem = null;
  const meridiemMatch = text.match(/(am|pm)\.?$/);
  if (meridiemMatch) {
    meridiem = meridiemMatch[1];
    text = text.slice(0, meridiemMatch.index);
  }

  let hours;
  let minutes;

  const separated = text.match(/^(\d{1,2})[:.h;](\d{1,2})$/);
  const compact = text.match(/^(\d{3,4})$/);
  const hourOnly = text.match(/^(\d{1,2})$/);

  if (separated) {
    hours = Number(separated[1]);
    minutes = Number(separated[2]);
  } else if (compact) {
    const digits = compact[1].padStart(4, '0');
    hours = Number(digits.slice(0, 2));
    minutes = Number(digits.slice(2));
  } else if (hourOnly) {
    hours = Number(hourOnly[1]);
    minutes = 0;
  } else {
    return null;
  }

  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  if (minutes > 59) return null;

  if (meridiem === 'pm' && hours < 12) hours += 12;
  if (meridiem === 'am' && hours === 12) hours = 0;

  if (hours > 24) return null;
  if (hours === 24 && minutes > 0) return null;

  return hours * 60 + minutes;
}

/**
 * Format minutes-from-midnight as HH:MM.
 * Values past midnight wrap (1500 -> "01:00") unless `wrap` is false, in which
 * case 24:00+ is rendered as a >24 hour clock (useful in debug output).
 */
export function formatMinutes(totalMinutes, { wrap = true } = {}) {
  if (totalMinutes === null || totalMinutes === undefined || !Number.isFinite(totalMinutes)) {
    return '--:--';
  }
  let value = Math.round(totalMinutes);
  if (wrap) {
    value = ((value % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  }
  const hours = Math.floor(value / 60);
  const minutes = value % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/** Format a duration in minutes as "8 hrs" / "7 hrs 30 mins" / "45 mins". */
export function formatDuration(totalMinutes) {
  if (!Number.isFinite(totalMinutes)) return '-';
  const sign = totalMinutes < 0 ? '-' : '';
  const value = Math.abs(Math.round(totalMinutes));
  const hours = Math.floor(value / 60);
  const minutes = value % 60;
  if (hours === 0) return `${sign}${minutes} min${minutes === 1 ? '' : 's'}`;
  if (minutes === 0) return `${sign}${hours} hr${hours === 1 ? '' : 's'}`;
  return `${sign}${hours} hr${hours === 1 ? '' : 's'} ${minutes} min${minutes === 1 ? '' : 's'}`;
}

/** Compact duration used inside dense table cells: "8h", "7h30". */
export function formatDurationCompact(totalMinutes) {
  if (!Number.isFinite(totalMinutes)) return '-';
  const value = Math.abs(Math.round(totalMinutes));
  const hours = Math.floor(value / 60);
  const minutes = value % 60;
  return minutes === 0 ? `${hours}h` : `${hours}h${String(minutes).padStart(2, '0')}`;
}

/**
 * Normalise a shift end so it always sits after the start on the linear number
 * line. "16:00-00:00" becomes 960 -> 1440 (an eight hour evening shift that
 * finishes exactly at midnight), "22:00-08:00" becomes 1320 -> 1920.
 */
export function normaliseEndMinutes(startMinutes, endMinutes) {
  let end = endMinutes;
  while (end <= startMinutes) end += MINUTES_PER_DAY;
  return end;
}

const SHIFT_SPLIT_RE = /\s*(?:-{1,2}|–|\u2014|‒|―|−|to|until|till|→|>)\s*/i;

/**
 * Parse free text describing a shift.
 *
 * Returns `{ ok, startMinutes, endMinutes, durationMinutes, crossesMidnight, raw }`
 * or `{ ok: false, reason, raw }`. Recognises the "ABSENT" marker and blank
 * cells via {@link classifyShiftCell} - this function only handles real times.
 */
export function parseShiftText(input) {
  const raw = input === null || input === undefined ? '' : String(input);
  const text = raw.trim();
  if (!text) return { ok: false, reason: 'empty', raw };

  const parts = text.split(SHIFT_SPLIT_RE).filter((part) => part !== '');
  if (parts.length !== 2) {
    return { ok: false, reason: 'unrecognised-format', raw };
  }

  const startMinutes = parseTimeToMinutes(parts[0]);
  const endMinutesRaw = parseTimeToMinutes(parts[1]);
  if (startMinutes === null || endMinutesRaw === null) {
    return { ok: false, reason: 'unrecognised-time', raw };
  }
  if (startMinutes >= MINUTES_PER_DAY) {
    return { ok: false, reason: 'start-out-of-range', raw };
  }
  // Equal clock times are overwhelmingly a transcription error, not a
  // deliberate 24-hour shift. Treating 09:00-09:00 as overnight would make a
  // malformed rota cell look valid and incorrectly make it lunch-eligible.
  if (startMinutes === endMinutesRaw) {
    return { ok: false, reason: 'implausible-duration', raw };
  }

  const endMinutes = normaliseEndMinutes(startMinutes, endMinutesRaw);
  const durationMinutes = endMinutes - startMinutes;
  if (durationMinutes <= 0 || durationMinutes > MINUTES_PER_DAY) {
    return { ok: false, reason: 'implausible-duration', raw };
  }

  return {
    ok: true,
    startMinutes,
    endMinutes,
    durationMinutes,
    crossesMidnight: endMinutes > MINUTES_PER_DAY,
    raw,
  };
}

const ABSENT_TOKENS = new Set([
  'absent', 'abs', 'off', 'holiday', 'hol', 'annual leave', 'al', 'leave',
  'sick', 'sickness', 'a/l', 'n/a', 'na', 'rest', 'rest day', 'day off',
]);

/**
 * Classify a raw rota cell as 'blank' | 'absent' | 'shift' | 'unparsed'.
 * Used by the rota editor, the paste importer and the PDF importer alike so
 * every entry point agrees on what a cell means.
 */
export function classifyShiftCell(value) {
  const raw = value === null || value === undefined ? '' : String(value);
  const text = raw.trim();
  if (!text) return { kind: 'blank', raw };

  const normalised = text.toLowerCase().replace(/[.\-_]+$/g, '').trim();
  if (ABSENT_TOKENS.has(normalised)) return { kind: 'absent', raw };

  const parsed = parseShiftText(text);
  if (parsed.ok) return { kind: 'shift', raw, shift: parsed };
  return { kind: 'unparsed', raw, reason: parsed.reason };
}

/** Format a shift as "08:30 – 14:30" for display. */
export function formatShiftRange(startMinutes, endMinutes) {
  return `${formatMinutes(startMinutes)} – ${formatMinutes(endMinutes)}`;
}

/**
 * Generate candidate slot start times between two bounds (inclusive of both
 * ends when they land on the interval grid).
 */
export function slotsBetween(startMinutes, endMinutes, intervalMinutes) {
  const slots = [];
  if (!Number.isFinite(intervalMinutes) || intervalMinutes <= 0) return slots;
  const first = Math.ceil(startMinutes / intervalMinutes) * intervalMinutes;
  for (let slot = first; slot <= endMinutes; slot += intervalMinutes) {
    slots.push(slot);
  }
  return slots;
}

/** True when [aStart, aEnd) and [bStart, bEnd) share any minute. */
export function rangesOverlap(aStart, aEnd, bStart, bEnd) {
  return aStart < bEnd && bStart < aEnd;
}

/* -------------------------------------------------------------------------- */
/* Calendar dates                                                             */
/* -------------------------------------------------------------------------- */

/** Parse an ISO `YYYY-MM-DD` string into a UTC-anchored Date (no DST drift). */
export function parseIsoDate(iso) {
  if (iso instanceof Date) return new Date(Date.UTC(iso.getFullYear(), iso.getMonth(), iso.getDate()));
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso ?? '').trim());
  if (!match) return null;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Format a Date (or ISO string) back to `YYYY-MM-DD`. */
export function toIsoDate(date) {
  const parsed = date instanceof Date ? date : parseIsoDate(date);
  if (!parsed) return '';
  return parsed.toISOString().slice(0, 10);
}

/** Add whole days to an ISO date string, returning a new ISO date string. */
export function addDays(iso, days) {
  const date = parseIsoDate(iso);
  if (!date) return '';
  date.setUTCDate(date.getUTCDate() + days);
  return toIsoDate(date);
}

/** ISO date for a day key within the week commencing `weekCommencing`. */
export function dateForDayKey(weekCommencing, dayKey) {
  const index = DAY_KEYS.indexOf(dayKey);
  if (index < 0) return '';
  return addDays(weekCommencing, index);
}

/** Snap any date back to the Monday of its week. */
export function mondayOf(iso) {
  const date = parseIsoDate(iso);
  if (!date) return '';
  // getUTCDay(): 0 = Sunday .. 6 = Saturday. Monday-based offset:
  const offset = (date.getUTCDay() + 6) % 7;
  date.setUTCDate(date.getUTCDate() - offset);
  return toIsoDate(date);
}

/**
 * UK ordinal suffix. Handles the 11th/12th/13th exceptions that a naive
 * `n % 10` implementation gets wrong.
 */
export function ordinalSuffix(n) {
  const value = Math.abs(Math.trunc(Number(n)));
  if (!Number.isFinite(value)) return '';
  const lastTwo = value % 100;
  if (lastTwo >= 11 && lastTwo <= 13) return 'th';
  switch (value % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}

/** `27` -> `27th`. */
export function withOrdinal(n) {
  return `${n}${ordinalSuffix(n)}`;
}

/**
 * Discord/UK long form: "Monday 27th of July 2026".
 */
export function formatUkLongDate(iso) {
  const date = parseIsoDate(iso);
  if (!date) return '';
  const dayName = DAY_LABELS[DAY_KEYS[(date.getUTCDay() + 6) % 7]];
  const dayNumber = date.getUTCDate();
  return `${dayName} ${withOrdinal(dayNumber)} of ${MONTH_NAMES[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
}

/** Short UK form: "27 Jul 2026". */
export function formatUkShortDate(iso) {
  const date = parseIsoDate(iso);
  if (!date) return '';
  return `${date.getUTCDate()} ${MONTH_NAMES[date.getUTCMonth()].slice(0, 3)} ${date.getUTCFullYear()}`;
}

/** Header form used on day cards: "Mon 27 Jul". */
export function formatDayHeader(iso) {
  const date = parseIsoDate(iso);
  if (!date) return '';
  const dayKey = DAY_KEYS[(date.getUTCDay() + 6) % 7];
  return `${DAY_SHORT_LABELS[dayKey]} ${date.getUTCDate()} ${MONTH_NAMES[date.getUTCMonth()].slice(0, 3)}`;
}

/** "WC 27 July 2026" - matches the heading style of the source rota exports. */
export function formatWeekCommencing(iso) {
  const date = parseIsoDate(iso);
  if (!date) return '';
  return `WC ${date.getUTCDate()} ${MONTH_NAMES[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
}
